/**
 * PURE. «Кунсткамера» — the extreme DNA set of creatures §D2 (K1–K12, B2), built from presets so the golden strings stay
 * in sync with the content tables. P2's `src/creature/__fixtures__/kunstkamera.ts` re-exports these (BUILD-PLAN: the
 * fixtures folder is P2's; P1 needs K10 for milestone P1-M1).
 */
import type { CreatureDNA, KunstId } from '../../shared';
import { buildDNA } from './codec';
import { applyPreset, speciesFields } from './presets';

export interface KunstFixture {
  readonly id: KunstId;
  readonly description: string;
  readonly dna: CreatureDNA;
  /** Equipment that is not DNA (weapon chains, the boss root scale). */
  readonly equip?: { weaponChain?: boolean; rootScale?: number; weapon?: string };
}

const k = (id: KunstId, description: string, body: string, opts: { head?: string; look?: number }, preset: Record<string, string | number>, equip?: KunstFixture['equip']): KunstFixture => {
  const m = speciesFields(body, opts);
  applyPreset(m, { garment: 'none', cape: 'none', headwear: 'none', idleProp: 'none', ...preset });
  m.soulSeed = 4242;
  return { id, description, dna: buildDNA(m), equip };
};

/** creatures §D2 (K1–K12 + the B2 Snailord; B1 is the squid boss rig, not a DNA). */
export const KUNSTKAMERA: readonly KunstFixture[] = [
  k('K1', 'Pinhead on stilts', 'cat', {}, { headH: 0.26, headW: 0.20, legLen: 0.40, legs: 'stilts', torso: 'stick' }),
  k('K2', 'Big-head stub', 'cat', {}, { headH: 0.38, legLen: 0.26, legs: 'stubby', arms: 'trex', armLen: 0.32 }),
  k('K3', 'The fattest king', 'cat', { look: 2 }, { torso: 'barrel', belly: 'beer', bellySize: 1, chestW: 1.35, cape: 'ermine', headwear: 'crown' }),
  k('K4', 'Stick friar', 'cat', { look: 3 }, { torso: 'stick', chestW: 0.75, armThick: 0.6, garment: 'robe' }),
  k('K5', 'Snail knight full kit', 'snail', {}, { headwear: 'bascinet', cape: 'tattered' }, { weapon: 'lance' }),
  k('K6', 'Longest ears', 'hare', {}, { ears: 'long', earSize: 0.16, height: 0.88 }),
  k('K7', 'Wing-arms', 'owl', {}, { arms: 'wings', headwear: 'none' }),
  k('K8', 'Every chain at once (palette = 48)', 'goat', {}, { headwear: 'jester', garment: 'chasuble', cape: 'cloak', beard: 'waist', tail: 'endless', tailLen: 1.5 },
    { weaponChain: true, weapon: 'flail' }),
  k('K9', 'Digitigrade hooves', 'pig', {}, { legType: 'digitigrade', hands: 'hooves', feet: 'hooves', stanceW: 1.4, legs: 'hams', headwear: 'none' }),
  k('K10', 'Neutral reference', 'cat', { look: 0 }, {}),
  k('K11', 'Chimera', 'cat', { head: 'goat' }, { horns: 'ram', headwear: 'cowl' }),
  k('K12', 'Toad troubadour', 'toad', {}, { eyes: 'bulging', eyeHeight: 0.56, idleProp: 'lute', legs: 'bowlegged', headwear: 'chaperon' }),
  k('B2', 'Great Snailord (K5 at ×1.6)', 'snail', {}, { headwear: 'bascinet', cape: 'tattered' }, { weapon: 'lance', rootScale: 1.6 }),
];

export const kunst = (id: KunstId): KunstFixture => {
  const f = KUNSTKAMERA.find((x) => x.id === id);
  if (!f) throw new Error(`unknown Kunstkamera id ${id}`);
  return f;
};
